import { useState } from 'react';
import { useAccount } from 'wagmi';
import { waitForTransactionReceipt, getAccount } from '@wagmi/core';
import { wagmiConfig } from '../providers/WalletProvider';
import { long, short, checkUSDTApprovalNeeded, approveUSDTAndWait } from '../services/contractService';

/**
 * Custom hook for IO Trading Contract operations
 * Handles opening future positions on the blockchain
 */
export const useIOTradingContract = () => {
  const { address, isConnected } = useAccount();
  const [isPending, setIsPending] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Helper function to get user address reliably
   * Tries hook address first, then falls back to wagmi config
   * @returns {string|null} User address or null if not available
   */
  const getUserAddress = () => {
    // Try to get address from hook first
    if (address) {
      return address;
    }
    
    // Fallback: try to get from wagmi config
    try {
      const account = getAccount(wagmiConfig);
      return account?.address || null;
    } catch (err) {
      console.warn('Could not get address from wagmi config:', err);
      return null;
    }
  };

  /**
   * Check if USDT approval is needed
   * @param {string|number} amount - Amount in USDT
   * @returns {Promise<{needsApproval: boolean, usdtAddress: string, error?: string}>}
   */
  const checkApprovalNeeded = async (amount) => {
    if (!isConnected) {
      return { needsApproval: false, error: 'Wallet not connected' };
    }
    
    const userAddress = getUserAddress();
    console.log('checkApprovalNeeded - getUserAddress result:', userAddress, 'hook address:', address);
    
    if (!userAddress) {
      console.error('No address available for approval check');
      return { needsApproval: false, error: 'User address is required for USDT approval. Please reconnect your wallet.' };
    }

    try {
      console.log('Calling checkUSDTApprovalNeeded with:', { userAddress, amount });
      const approvalCheck = await checkUSDTApprovalNeeded(userAddress, amount);
      return { 
        needsApproval: approvalCheck.needsApproval, 
        usdtAddress: approvalCheck.usdtAddress 
      };
    } catch (err) {
      console.error('Error checking approval:', err);
      return { needsApproval: false, error: err.message || 'Error checking approval' };
    }
  };

  /**
   * Approve USDT spending
   * @param {string} usdtAddress - USDT token address
   * @returns {Promise<{success: boolean, hash?: string, error?: string}>}
   */
  const approveUSDT = async (usdtAddress, amount) => {
    if (!isConnected) {
      return { success: false, error: 'Wallet not connected' };
    }
    
    const userAddress = getUserAddress();
    if (!userAddress) {
      return { success: false, error: 'User address is required for USDT approval' };
    }

    try {
      setIsPending(true);
      setIsConfirming(false);
      setIsConfirmed(false);
      setError(null);

      const result = await approveUSDTAndWait(usdtAddress, amount);
      
      setIsPending(false);
      setIsConfirming(false);
      setIsConfirmed(true);

      return { success: true, hash: result.hash };
    } catch (err) {
      console.error('Error approving USDT:', err);
      setError(err.message || 'Approval failed');
      setIsPending(false);
      setIsConfirming(false);
      setIsConfirmed(false);
      return { success: false, error: err.message || 'Approval failed' };
    }
  };

  /**
   * Open a future position on the blockchain
   * @param {string} crypto - Crypto symbol (BTC, ETH, BNB, USDT)
   * @param {number} entryPrice - Entry price
   * @param {number} duration - Duration in seconds
   * @param {boolean} isLong - True for LONG, false for SHORT
   * @param {string|number} amount - Amount in BNB or USDT (as string or number) - this should be position size (amount * leverage)
   * @param {boolean} stakeUSDT - Whether to stake in USDT (true) or BNB (false)
   * @param {boolean} skipApproval - Skip approval check (use when approval already handled separately)
   * @param {number} leverage - Leverage multiplier (e.g., 10 for 10x)
   * @returns {Promise<{success: boolean, hash?: string, error?: string}>}
   */
  const openFuturePosition = async (crypto, entryPrice, duration = 3600, isLong, amount, stakeUSDT = false, skipApproval = false, leverage = 1) => {
    if (!isConnected) {
      return { success: false, error: 'Wallet not connected' };
    }

    const userAddress = getUserAddress();
    if (!userAddress) {
      return { success: false, error: 'User address is required. Please reconnect your wallet.' };
    }

    try {
      setIsPending(true);
      setIsConfirming(false);
      setIsConfirmed(false);
      setError(null);

      // Use the new contract functions (skip approval if already handled)
      // Pass leverage value to contract functions
      const hash = isLong 
        ? await long(crypto, entryPrice, duration, stakeUSDT, amount, userAddress, skipApproval, leverage)
        : await short(crypto, entryPrice, duration, stakeUSDT, amount, userAddress, skipApproval, leverage);

      setIsPending(false);
      setIsConfirming(true);

      // Wait for transaction receipt
      const receipt = await waitForTransactionReceipt(wagmiConfig, { hash });

      setIsConfirming(false);
      setIsConfirmed(true);

      return { success: true, hash: hash };
    } catch (err) {
      console.error('Error opening future position:', err);
      setError(err.message || 'Transaction failed');
      setIsPending(false);
      setIsConfirming(false);
      setIsConfirmed(false);
      return { success: false, error: err.message || 'Transaction failed' };
    }
  };

  /**
   * Reset transaction state
   */
  const resetState = () => {
    setIsPending(false);
    setIsConfirming(false);
    setIsConfirmed(false);
    setError(null);
  };

  return {
    openFuturePosition,
    checkApprovalNeeded,
    approveUSDT,
    isPending,
    isConfirming,
    isConfirmed,
    error,
    resetState,
    address,
    isConnected,
  };
};
